import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { Badge } from './badge';
import { Button } from './button';
import { HoverLift, GradientShift, PulseGlow } from './motion-primitives';

interface BlogCardProps {
  post: {
    id: string;
    title: string;
    excerpt: string;
    date: string;
    readTime: string;
    tags: string[];
    slug: string;
    author: string;
  };
  index?: number;
  featured?: boolean;
}

export const BlogCard: React.FC<BlogCardProps> = ({
  post,
  index = 0,
  featured = false
}) => {
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: featured ? 1 : 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        delay: index * 0.1,
        ease: [0.4, 0, 0.2, 1] as const
      }
    }
  };

  if (featured) {
    return (
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        className="group relative overflow-hidden bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-xl"
      >
        <HoverLift liftAmount={6}>
        <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-10 transition-all duration-300" />
        <div className="relative bg-white bg-opacity-10 backdrop-blur-sm p-1">
          <div className="bg-white rounded-xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="flex items-center gap-3 mb-6"
                >
                  <span className="px-4 py-2 text-sm font-bold bg-indigo-100 text-indigo-700 rounded-full">
                    ⭐ Latest Feature
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs font-medium bg-indigo-50 text-indigo-600 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 hover:text-indigo-600 transition-colors leading-tight"
                >
                  <GradientShift colors={["from-purple-600", "to-indigo-600"]}>
                    <Link to={`/blog/${post.slug}`}>
                      {post.title}
                    </Link>
                  </GradientShift>
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="text-lg text-gray-600 mb-8 leading-relaxed"
                >
                  {post.excerpt}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {new Date(post.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {post.readTime}
                    </span>
                    <span>by {post.author}</span>
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="flex justify-center lg:justify-end"
              >
                <PulseGlow color="purple" intensity={0.4}>
                  <Button asChild size="lg" className="bg-indigo-600 hover:bg-indigo-700 px-8">
                    <Link to={`/blog/${post.slug}`} className="flex items-center">
                      Read Full Post
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </Button>
                </PulseGlow>
              </motion.div>
            </div>
          </div>
        </div>
        </HoverLift>
      </motion.div>
    );
  }

  return (
    <motion.article
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      className="group bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200 hover:border-indigo-200"
    >
      <HoverLift liftAmount={6}>
      <div className="p-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.1 + 0.2, duration: 0.3 }}
          className="flex flex-wrap gap-2 mb-4"
        >
          {post.tags.slice(0, 2).map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
            >
              {tag}
            </Badge>
          ))}
        </motion.div>

        <motion.h3
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 + 0.3, duration: 0.3 }}
          className="text-xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors leading-tight"
        >
          <GradientShift colors={["from-purple-600", "to-indigo-600"]}>
            <Link to={`/blog/${post.slug}`}>
              {post.title}
            </Link>
          </GradientShift>
        </motion.h3>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.1 + 0.4, duration: 0.3 }}
          className="text-gray-600 mb-4 leading-relaxed text-sm line-clamp-3"
        >
          {post.excerpt}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 + 0.5, duration: 0.3 }}
          className="flex items-center justify-between text-xs text-gray-500"
        >
          <div className="flex items-center space-x-3">
            <span className="flex items-center">
              <Calendar className="w-3 h-3 mr-1" />
              {new Date(post.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })}
            </span>
            <span className="flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {post.readTime}
            </span>
          </div>

          <PulseGlow color="purple" intensity={0.3}>
            <Button variant="ghost" size="sm" asChild className="p-0 h-auto">
              <Link to={`/blog/${post.slug}`} className="flex items-center text-indigo-600 hover:text-indigo-700">
                Read
                <ArrowRight className="w-3 h-3 ml-1" />
              </Link>
            </Button>
          </PulseGlow>
        </motion.div>
      </div>
      </HoverLift>
    </motion.article>
  );
};

export default BlogCard;
